import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { ThirdPartyDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { ThirdPartyResponseRow } from '@risksmart-app/drizzle/src/dto';
import type {
  GetThirdPartiesQuery,
  Third_Party_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetThirdPartiesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

import { THIRD_PARTY_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  departmentsWhereOnEntity,
  tagsWhereOnEntity,
  toTrpcDashboardFilterInput,
} from '@/hooks/queries/dashboardFilterArgs';
import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

const useThirdPartyRegisterGraphqlVariables = (
  args: UseGetThirdPartyRegisterArgs
) => {
  const { user } = useRisksmartUser();
  const entityWhere = useEntityWhereFilter<Third_Party_Bool_Exp>(
    Parent_Type_Enum.ThirdParty
  );

  const { where } = buildDashboardWhere({
    base: entityWhere,
    filters: args,
    userId: user?.userId,
    datePredicates: THIRD_PARTY_DATE_PREDICATES,
    tagsWhere: tagsWhereOnEntity,
    departmentsWhere: departmentsWhereOnEntity,
    ownershipWhere: (conditions) => conditions.thirdPartyFilterConditions,
  });

  return { where };
};

export type UseGetThirdPartyRegisterArgs =
  DashboardFilterArgs<ThirdPartyDashboardDateField>;

export const useGetThirdPartyRegister = createQueryHook<
  UseGetThirdPartyRegisterArgs,
  ThirdPartyResponseRow[],
  GetThirdPartiesQuery
>({
  trpcQueryOptions: (
    trpc,
    { tagTypeIds, departmentTypeIds, dateRange, ownership }
  ) =>
    trpc.frontend.thirdParty.register.queryOptions(
      toTrpcDashboardFilterInput({
        tagTypeIds,
        departmentTypeIds,
        dateRange,
        ownership,
      })
    ),
  mapTrpcDataToGraphQL: (data) => ({ third_party: data }),
  graphqlDocument: GetThirdPartiesDocument,
  graphqlVariables: useThirdPartyRegisterGraphqlVariables,
});
